import { IAlertCenter } from "../interfaces/IAlertCenter";
import { ActionResult } from "../interfaces/ActionResult";
import { AlertType, AlertLevel } from "../types/AlertTypes";

export type AddAlert = (
  key: string,
  alert: ActionResult,
  timeout?: number
) => ActionResult;

export class AlertCenter implements IAlertCenter {
  ruleModuleAlerts: { [key: string]: ActionResult } = {};
  ruleItemAlerts: { [key: string]: ActionResult } = {};

  alertHistory: { [key: string]: ActionResult[] } = {};

  alertTimers: { [key: string]: ReturnType<typeof setTimeout> } = {};

  constructor(externalCallback: () => void) {
    this.externalCallback = externalCallback;
  }

  public externalCallback = () => {
    alert("AlertCenter external callback not implemented for this instance!");
  };

  public getKey = (type: AlertType, key: string): string => {
    return `${key}_${type}`;
  };

  private addAlert = (
    type: AlertType,
    key: string,
    alert: ActionResult,
    timeout = 3000
  ): ActionResult => {
    if (Object.keys(alert).includes("triggerUI") && !alert.triggerUI) {
      return {
        success: true,
        message: "This UI message is disabled.",
        level: AlertLevel._0_CRITICAL,
      };
    }
    const alertKey = this.getKey(type, key);
    if (alertKey in this.alertTimers) {
      clearTimeout(this.alertTimers[alertKey]);
    }
    this.alertTimers[alertKey] = setTimeout(() => {
      if (type === AlertType.MODULE) {
        delete this.ruleModuleAlerts[key];
      } else {
        delete this.ruleItemAlerts[key];
      }
      this.externalCallback();
    }, timeout);
    if (type === AlertType.MODULE) {
      this.ruleModuleAlerts[key] = alert;
    } else {
      this.ruleItemAlerts[key] = alert;
    }
    const previousAlerts = this.getAlerts(
      type,
      key,
      true,
      AlertLevel._5_VERBOSE
    );
    if (previousAlerts[previousAlerts.length - 1]?.message !== alert.message) {
      this.alertHistory[alertKey] = [...previousAlerts, alert];
    }
    this.externalCallback();
    return alert;
  };

  public addModuleAlert: AddAlert = (key, alert, timeout) => {
    return this.addAlert(AlertType.MODULE, key, alert, timeout);
  };

  public addItemAlert: AddAlert = (key, alert, timeout) => {
    return this.addAlert(AlertType.ITEM, key, alert, timeout);
  };

  private getAlertsHistory = (type: AlertType, key: string): ActionResult[] => {
    const alertKey = this.getKey(type, key);
    if (alertKey in this.alertHistory) {
      return this.alertHistory[alertKey];
    }
    return [];
  };

  public getAlerts = (
    type: AlertType,
    key: string,
    showAll: boolean,
    level: AlertLevel
  ) => {
    let activeAlerts: ActionResult[] = [];
    if (showAll) {
      activeAlerts = this.getAlertsHistory(type, key);
    } else {
      if (type === AlertType.MODULE && key in this.ruleModuleAlerts) {
        activeAlerts = [this.ruleModuleAlerts[key]];
      }
      if (type === AlertType.ITEM && key in this.ruleItemAlerts) {
        activeAlerts = [this.ruleItemAlerts[key]];
      }
    }

    return activeAlerts.filter((alert) => alert.level <= level);
  };
}
